import * as nidus from "nidus";

import { reject } from "../TypingUtils";
import { Fetch } from "../HttpClient/FetchImplementation/Fetch";
import { AuthenticationMode, createAuthenticationProvider } from "../HttpClient/AuthenticationMode";
import { ExceptionLogger, HttpClient } from "../HttpClient/HttpClient";
import { IHttpClient } from "../HttpClient/IHttpClient";

import {
    INidusClient,
    NidusEntry,
    NidusEntryData,
    NidusEntryId,
    NidusName,
    NidusReferenceKey,
    NidusSearchResponse,
    parseEntryId,
} from "./INidusClient";

interface NativeNidusSearchResponse {
    items: nidus.NidusEntry[];
    totalSuitableCount: number;
}

export class NidusClientImpl implements INidusClient {
    private readonly httpClient: IHttpClient;
    private readonly client: nidus.NidusClient;

    public constructor(
        fetchImpl: Fetch,
        baseUrl: string,
        authentication?: AuthenticationMode,
        exceptionLogger?: ExceptionLogger
    ) {
        this.httpClient = new HttpClient(
            fetchImpl,
            baseUrl,
            createAuthenticationProvider(authentication),
            undefined,
            exceptionLogger
        );
        this.client = new nidus.NidusClient("", { fetch: this.httpClient.createFetchImpl() });
    }

    public batchInsert(): Promise<void> {
        return Promise.resolve(undefined);
    }

    public async delete(nidusName: NidusName, key: NidusReferenceKey, objectId: NidusEntryId): Promise<void> {
        await this.client.delete(nidusName, key, objectId);
    }

    public async insert(nidusName: NidusName, key: NidusReferenceKey, obj: NidusEntryData): Promise<NidusEntry> {
        const nativeEntry = await this.client.insert(nidusName, key, obj);
        return NidusClientImpl.nativeEntryToEntry(nativeEntry);
    }

    private static nativeEntryToEntry(nativeEntry: nidus.NidusEntry): NidusEntry {
        return {
            id: parseEntryId(nativeEntry.id ?? reject("Nidus entry id does not contain id")),
            data: nativeEntry.data ?? {},
        };
    }

    public async search(
        nidusName: NidusName,
        key: NidusReferenceKey,
        searchValue: string | null | undefined
    ): Promise<NidusSearchResponse> {
        const result = await this.httpClient
            .get(`/nidus/${nidusName}/search`)
            .withQueryParams({ key: key, searchValue: searchValue })
            .json<NativeNidusSearchResponse>();
        return {
            items: result.items.map(NidusClientImpl.nativeEntryToEntry),
            totalSuitableCount: result.totalSuitableCount,
        };
    }

    public async searchMinitour(
        nidusName: NidusName,
        key: NidusReferenceKey,
        searchValue: string | null | undefined
    ): Promise<NidusSearchResponse> {
        const result = await this.httpClient
            .get(`/nidus/${nidusName}/searchMinitour`)
            .withQueryParams({ key: key, searchValue: searchValue })
            .json<NativeNidusSearchResponse>();
        return {
            items: result.items.map(NidusClientImpl.nativeEntryToEntry),
            totalSuitableCount: result.totalSuitableCount,
        };
    }

    public async update(
        nidusName: NidusName,
        key: NidusReferenceKey,
        objectId: NidusEntryId,
        obj: NidusEntryData
    ): Promise<void> {
        await this.client.update(nidusName, key, objectId, obj);
    }
}
